/**
 * Video Settings Inspector Component
 * 
 * Bottom panel inspector showing timeline first, then video-level settings when no segment is selected.
 * Same content as VideoSettingsPanel but optimized for inspector view.
 */

import React from 'react';
import { OverlayConfig, IntroClipConfig, OutroClipConfig } from '../../../types/overlayConfig';
import ScoreOverlayConfig from '../../overlay-editor/ScoreOverlayConfig';
import WatermarkConfig from '../../overlay-editor/WatermarkConfig';
import IntroConfig from '../../overlay-editor/IntroConfig';
import OutroConfig from '../../overlay-editor/OutroConfig';
import IntroOutroStaticPreview from '../../overlay-editor/IntroOutroStaticPreview';

interface VideoSettingsInspectorProps {
  overlayConfig: OverlayConfig;
  onOverlayConfigChange: (config: OverlayConfig) => void;
  matchId: string;
}

const VideoSettingsInspector: React.FC<VideoSettingsInspectorProps> = React.memo(({
  overlayConfig,
  onOverlayConfigChange,
  matchId,
}) => {
  const [scoreOverlayExpanded, setScoreOverlayExpanded] = React.useState(false);
  const [watermarkExpanded, setWatermarkExpanded] = React.useState(false);
  const [introExpanded, setIntroExpanded] = React.useState(false);
  const [outroExpanded, setOutroExpanded] = React.useState(false);
  
  // Temporarily disable these features until workers are ready
  const isScoreOverlayDisabled = false; // Enabled for testing
  const isIntroClipsDisabled = false; // Enabled for testing
  const isOutroClipsDisabled = false; // Enabled for testing

  const aspectRatioOptions = [
    { value: 'original', label: 'Original' },
    { value: '16:9', label: '16:9 (Widescreen)' },
    { value: '9:16', label: '9:16 (Vertical)' },
    { value: '1:1', label: '1:1 (Square)' },
    { value: '4:3', label: '4:3 (Classic)' },
  ];

  return (
    <div className="flex flex-col bg-white">
      {/* Header */}
      <div className="px-3 py-1.5 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <h3 className="text-xs font-medium text-gray-700">Video Settings</h3>
      </div>

      {/* Content - No scroll, expands naturally */}
      <div className="p-3 space-y-3">
        {/* Aspect Ratio Section */}
        <div className="border border-gray-200 rounded-lg p-2.5 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <label className="flex items-center space-x-2 cursor-pointer flex-1">
              <span className="text-xs font-medium text-gray-900">Aspect Ratio</span>
            </label>
          </div>
          <select
            value={overlayConfig.aspectRatio || 'original'}
            onChange={(e) => {
              onOverlayConfigChange({
                ...overlayConfig,
                aspectRatio: e.target.value as 'original' | '16:9' | '9:16' | '1:1' | '4:3',
              });
            }}
            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {aspectRatioOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Score Overlay Section */}
        <div className={`border border-gray-200 rounded-lg p-2.5 bg-gray-50 ${isScoreOverlayDisabled ? 'opacity-60' : ''}`}>
          <div className="flex items-center justify-between mb-2">
            <label className={`flex items-center space-x-2 flex-1 ${isScoreOverlayDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
              <input
                type="checkbox"
                checked={overlayConfig.scoreOverlay?.enabled || false}
                onChange={(e) => !isScoreOverlayDisabled && onOverlayConfigChange({
                  ...overlayConfig,
                scoreOverlay: {
                  ...overlayConfig.scoreOverlay,
                  enabled: e.target.checked,
                  preset: overlayConfig.scoreOverlay?.preset || 'preset1',
                  position: overlayConfig.scoreOverlay?.position || 'top-right',
                  colors: overlayConfig.scoreOverlay?.colors || {
                    primary: '#0B66FF',
                    secondary: '#FFFFFF',
                    textColor: '#111827',
                  },
                  opacity: overlayConfig.scoreOverlay?.opacity ?? 1.0,
                  gradient: overlayConfig.scoreOverlay?.gradient || {
                    enabled: false,
                    type: 'horizontal',
                    direction: 0,
                    startColor: '#0B66FF',
                    endColor: '#FFFFFF',
                    stops: [50, 50],
                  },
                  showTournamentLogo: overlayConfig.scoreOverlay?.showTournamentLogo ?? false,
                  showMatchTime: overlayConfig.scoreOverlay?.showMatchTime ?? true,
                  updateOnGoals: overlayConfig.scoreOverlay?.updateOnGoals ?? true,
                },
                })}
                disabled={isScoreOverlayDisabled}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isScoreOverlayDisabled) e.preventDefault();
                }}
              />
              <span className={`text-xs ${isScoreOverlayDisabled ? 'text-gray-400' : 'text-gray-700'}`}>Score Overlay</span>
            </label>
            <button
              onClick={() => !isScoreOverlayDisabled && setScoreOverlayExpanded(!scoreOverlayExpanded)}
              disabled={isScoreOverlayDisabled}
              className={`p-1 text-gray-500 transition-colors ${isScoreOverlayDisabled ? 'cursor-not-allowed opacity-50' : 'hover:text-gray-700'}`}
              aria-label={scoreOverlayExpanded ? 'Collapse' : 'Expand'}
            >
              <svg
                className={`w-4 h-4 transition-transform ${scoreOverlayExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          
          {scoreOverlayExpanded && !isScoreOverlayDisabled && (
            <div className="mt-2">
              <ScoreOverlayConfig
                config={overlayConfig.scoreOverlay || {
                  enabled: false,
                  preset: 'preset1',
                  position: 'top-right',
                  colors: {
                    primary: '#0B66FF',
                    secondary: '#FFFFFF',
                    textColor: '#111827',
                  },
                  opacity: 1.0,
                  gradient: {
                    enabled: false,
                    type: 'horizontal',
                    direction: 0,
                    startColor: '#0B66FF',
                    endColor: '#FFFFFF',
                    stops: [50, 50],
                  },
                  showTournamentLogo: false,
                  showMatchTime: true,
                  updateOnGoals: true,
                }}
                onChange={(scoreConfig) => onOverlayConfigChange({
                  ...overlayConfig,
                  scoreOverlay: scoreConfig,
                })}
              />
            </div>
          )}
        </div>

        {/* Watermark Section */}
        <div className="border border-gray-200 rounded-lg p-2.5 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <label className="flex items-center space-x-2 cursor-pointer flex-1">
              <input
                type="checkbox"
                checked={overlayConfig.watermark?.enabled || false}
                onChange={(e) => onOverlayConfigChange({
                  ...overlayConfig,
                  watermark: {
                    ...overlayConfig.watermark,
                    enabled: e.target.checked,
                    logoType: overlayConfig.watermark?.logoType || 'mark',
                    position: overlayConfig.watermark?.position || 'bottom-right',
                    opacity: overlayConfig.watermark?.opacity ?? 0.8,
                    size: overlayConfig.watermark?.size || 'medium',
                  },
                })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
              <span className="text-xs text-gray-700">Watermark</span>
            </label>
            <button
              onClick={() => setWatermarkExpanded(!watermarkExpanded)}
              className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label={watermarkExpanded ? 'Collapse' : 'Expand'}
            >
              <svg
                className={`w-4 h-4 transition-transform ${watermarkExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          
          {watermarkExpanded && (
            <div className="mt-2">
              <WatermarkConfig
                config={overlayConfig.watermark || {
                  enabled: false,
                  logoType: 'mark',
                  position: 'bottom-right',
                  opacity: 0.8,
                  size: 'medium',
                }}
                onChange={(watermarkConfig) => onOverlayConfigChange({
                  ...overlayConfig,
                  watermark: watermarkConfig,
                })}
              />
            </div>
          )}
        </div>

        {/* Intro Clips Section */}
        <div className={`border border-gray-200 rounded-lg p-2.5 bg-gray-50 ${isIntroClipsDisabled ? 'opacity-60' : ''}`}>
          <div className="flex items-center justify-between mb-2">
            <label className={`flex items-center space-x-2 flex-1 ${isIntroClipsDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
              <input
                type="checkbox"
                checked={overlayConfig.introOutro?.intro?.enabled || false}
                onChange={(e) => !isIntroClipsDisabled && onOverlayConfigChange({
                  ...overlayConfig,
                  introOutro: {
                    ...overlayConfig.introOutro,
                    intro: {
                      ...(overlayConfig.introOutro?.intro || {
                        enabled: false,
                        duration: 5,
                        preset: 'preset1',
                        textColor: '#FFFFFF',
                        backgroundColor: '#000000',
                        opacity: 1.0,
                        gradient: {
                          enabled: false,
                          type: 'horizontal',
                          direction: 0,
                          startColor: '#000000',
                          endColor: '#FFFFFF',
                          stops: [50, 50],
                        },
                        showTournamentLogo: true,
                        showTeamLogos: true,
                        showLineups: true,
                      }),
                      enabled: e.target.checked,
                    },
                    outro: overlayConfig.introOutro?.outro,
                  },
                })}
                disabled={isIntroClipsDisabled}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isIntroClipsDisabled) e.preventDefault();
                }}
              />
              <span className={`text-xs ${isIntroClipsDisabled ? 'text-gray-400' : 'text-gray-700'}`}>Intro Clips</span>
            </label>
            <button
              onClick={() => !isIntroClipsDisabled && setIntroExpanded(!introExpanded)}
              disabled={isIntroClipsDisabled}
              className={`p-1 text-gray-500 transition-colors ${isIntroClipsDisabled ? 'cursor-not-allowed opacity-50' : 'hover:text-gray-700'}`}
              aria-label={introExpanded ? 'Collapse' : 'Expand'}
            >
              <svg
                className={`w-4 h-4 transition-transform ${introExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          
          {introExpanded && !isIntroClipsDisabled && (
            <div className="mt-2 space-y-3">
              {/* Preview - only show if enabled */}
              {overlayConfig.introOutro?.intro?.enabled && (
                <div className="flex justify-center pb-2 border-b border-gray-200">
                  <IntroOutroStaticPreview
                    introConfig={overlayConfig.introOutro.intro}
                    type="intro"
                    aspectRatio={overlayConfig.aspectRatio || 'original'}
                  />
                </div>
              )}
              
              {/* Configuration */}
              <IntroConfig
                introConfig={overlayConfig.introOutro?.intro || {
                  enabled: false,
                  duration: 5,
                  preset: 'preset1',
                  textColor: '#FFFFFF',
                  backgroundColor: '#000000',
                  opacity: 1.0,
                  gradient: {
                    enabled: false,
                    type: 'horizontal',
                    direction: 0,
                    startColor: '#000000',
                    endColor: '#FFFFFF',
                    stops: [50, 50],
                  },
                  showTournamentLogo: true,
                  showTeamLogos: true,
                  showLineups: true,
                }}
                onIntroChange={(introConfig: IntroClipConfig) => onOverlayConfigChange({
                  ...overlayConfig,
                  introOutro: {
                    ...overlayConfig.introOutro,
                    intro: introConfig,
                    outro: overlayConfig.introOutro?.outro,
                  },
                })}
              />
            </div>
          )}
        </div>

        {/* Outro Clips Section */}
        <div className={`border border-gray-200 rounded-lg p-2.5 bg-gray-50 ${isOutroClipsDisabled ? 'opacity-60' : ''}`}>
          <div className="flex items-center justify-between mb-2">
            <label className={`flex items-center space-x-2 flex-1 ${isOutroClipsDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
              <input
                type="checkbox"
                checked={overlayConfig.introOutro?.outro?.enabled || false}
                onChange={(e) => !isOutroClipsDisabled && onOverlayConfigChange({
                  ...overlayConfig,
                  introOutro: {
                    ...overlayConfig.introOutro,
                    intro: overlayConfig.introOutro?.intro,
                    outro: {
                      ...(overlayConfig.introOutro?.outro || {
                        enabled: false,
                        duration: 3,
                        textColor: '#FFFFFF',
                        backgroundColor: '#000000',
                        opacity: 1.0,
                        gradient: {
                          enabled: false,
                          type: 'horizontal',
                          direction: 0,
                          startColor: '#000000',
                          endColor: '#FFFFFF',
                          stops: [50, 50],
                        },
                        showTournamentLogo: true,
                        showThanksMessage: true,
                      }),
                      enabled: e.target.checked,
                    },
                  },
                })}
                disabled={isOutroClipsDisabled}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isOutroClipsDisabled) e.preventDefault();
                }}
              />
              <span className={`text-xs ${isOutroClipsDisabled ? 'text-gray-400' : 'text-gray-700'}`}>Outro Clips</span>
            </label>
            <button
              onClick={() => !isOutroClipsDisabled && setOutroExpanded(!outroExpanded)}
              disabled={isOutroClipsDisabled}
              className={`p-1 text-gray-500 transition-colors ${isOutroClipsDisabled ? 'cursor-not-allowed opacity-50' : 'hover:text-gray-700'}`}
              aria-label={outroExpanded ? 'Collapse' : 'Expand'}
            >
              <svg
                className={`w-4 h-4 transition-transform ${outroExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          
          {outroExpanded && !isOutroClipsDisabled && (
            <div className="mt-2 space-y-3">
              {/* Preview - only show if enabled */}
              {overlayConfig.introOutro?.outro?.enabled && (
                <div className="flex justify-center pb-2 border-b border-gray-200">
                  <IntroOutroStaticPreview
                    outroConfig={overlayConfig.introOutro.outro}
                    type="outro"
                    aspectRatio={overlayConfig.aspectRatio || 'original'}
                  />
                </div>
              )}
              
              {/* Configuration */}
              <OutroConfig
                outroConfig={overlayConfig.introOutro?.outro || {
                  enabled: false,
                  duration: 3,
                  textColor: '#FFFFFF',
                  backgroundColor: '#000000',
                  opacity: 1.0,
                  gradient: {
                    enabled: false,
                    type: 'horizontal',
                    direction: 0,
                    startColor: '#000000',
                    endColor: '#FFFFFF',
                    stops: [50, 50],
                  },
                  showTournamentLogo: true,
                  showThanksMessage: true,
                }}
                onOutroChange={(outroConfig: OutroClipConfig) => onOverlayConfigChange({
                  ...overlayConfig,
                  introOutro: {
                    ...overlayConfig.introOutro,
                    intro: overlayConfig.introOutro?.intro,
                    outro: outroConfig,
                  },
                })}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

VideoSettingsInspector.displayName = 'VideoSettingsInspector';

export default VideoSettingsInspector;
